export enum FormFieldType {
  INPUT = 'input',
  SELECT = 'select',
  RADIO = 'radio',
  FIELD_ARRAY = 'field_array',
  SCALE_RATING = 'scale_rating',
  CSV_LOOKUP = 'csv_lookup',
  PAGE_BREAK = 'page_break',
}

export interface InputOptions {
  maxValue?: number;
  minValue?: number;
  integer?: boolean;
  maxLength?: number;
  minLength?: number;
  type: 'text' | 'email' | 'number';
}

export interface SelectOptions {
  options: string[];
  multiple?: boolean;
  explanation?: string;
  correctOptions?: number[];
}

export interface RadioOptions {
  options: string[];
}

export interface ScaleRatingOptions {
  minValue: number;
  maxValue: number;
  options: string[];
  valueLabels?: string[];
}

export interface FieldArrayOptions {
  fields: FormField[];
  layout?: 'vertical' | 'horizontal';
}

export enum CsvLookupPreset {
  SCHOOL_LIST = 'school_list',
}

export interface CsvLookupOptions {
  // Server-managed lookup configuration. The client searches through the
  // form/field endpoint, which resolves these values from the saved form.
  datasetId: string;
  itemsFormId: string;
  preset: CsvLookupPreset;
}
export interface CsvLookupReferenceValue {
  referenceItemId: string;
}
export interface CsvLookupOptionValue {
  id: string;
  searchValue: string;
  displayValue: string;
}
export interface CsvLookupStoredValue {
  datasetId: string;
  searchValue: string;
  displayValue: string;
  referenceItemId: string;
  snapshot: Record<string, string | null>;
}

export type FormFieldTranslation = {
  label: string;
  placeholder?: string;
};

export type FormFieldTranslationWith<T> = FormFieldTranslation & T;

interface BaseFormField {
  id: string;
  label: string;
  required?: boolean;
  placeholder?: string;
}

export interface InputFormField extends BaseFormField {
  options?: InputOptions;
  type: FormFieldType.INPUT;
  translations?: Record<string, FormFieldTranslation>;
}

export interface RadioFormField extends BaseFormField {
  options?: RadioOptions;
  type: FormFieldType.RADIO;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<RadioOptions, 'options'>>
  >;
}

export interface SelectFormField extends BaseFormField {
  options?: SelectOptions;
  type: FormFieldType.SELECT;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<SelectOptions, 'options' | 'explanation'>>
  >;
}

export interface ScaleRatingFormField extends BaseFormField {
  options?: ScaleRatingOptions;
  type: FormFieldType.SCALE_RATING;
  translations?: Record<
    string,
    FormFieldTranslationWith<
      Pick<ScaleRatingOptions, 'options' | 'valueLabels'>
    >
  >;
}

export interface FieldArrayFormField extends BaseFormField {
  options?: FieldArrayOptions;
  type: FormFieldType.FIELD_ARRAY;
}

export interface PageBreakFormField extends BaseFormField {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  options?: {};
  type: FormFieldType.PAGE_BREAK;
}
export interface CsvLookupFormField extends BaseFormField {
  options: CsvLookupOptions;
  type: FormFieldType.CSV_LOOKUP;
}

export type FormField =
  | InputFormField
  | RadioFormField
  | SelectFormField
  | PageBreakFormField
  | CsvLookupFormField
  | FieldArrayFormField
  | ScaleRatingFormField;

export interface FormBlock {
  id: string;
  name: string;
  fields: FormField[];
}

export interface FormData {
  blocks: FormBlock[];
}

export interface FormSubmissionData {
  [fieldId: string]:
    | string
    | number
    | string[]
    | FormSubmissionData
    | Array<number | null>
    | CsvLookupStoredValue[];
}

export interface FormSubmissionFormData {
  [fieldId: string]:
    | string
    | number
    | string[]
    | Array<number | null>
    | FormSubmissionFormData
    | Array<
        CsvLookupOptionValue | CsvLookupStoredValue | CsvLookupReferenceValue
      >;
}

export interface FormSubmissionPayloadData {
  [fieldId: string]:
    | string
    | number
    | string[]
    | Array<number | null>
    | FormSubmissionPayloadData
    | CsvLookupReferenceValue[];
}

export interface FormSubmission {
  id: string;
  userId: string;
  formId: string;
  createdAt: string;
  updatedAt: string;
  data: FormSubmissionData;
}

export interface ValidationError {
  fieldId: string;
  message: string;
}

export interface Form {
  id: string;
  data: FormData;
  version: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  courseStepId: string | null;
}

export interface FormSubmissionPayload {
  formId: string;
  isSubmitted: boolean;
  data: FormSubmissionPayloadData;
}
